/**
 * Les lignes de la session qui naissent d'une génération, et l'historique un niveau au-dessus (Wajih, 04/11), en
 * version compacte, le détail à la demande :
 *  - **la ligne vivante** (`progress`) : une ligne qui dit l'étape en cours et les secondes, la barre qui file
 *    dessous ; un appui montre toutes les étapes ;
 *  - **la carte « prêtes »** (`ready`) : encadrée (le cadre désigne la prochaine action), courte et actionnable,
 *    la relecture se fait au centre ; survoler une proposition en montre l'aperçu au centre (web) ;
 *  - **« <champion> propose »** (`proposal`) : la seule carte colorée (l'aurore du champion), signée de sa tête ;
 *    une ligne pour l'activité, une pour sa raison, « Voir » et l'envoi d'un geste ; un appui sur la raison la
 *    déplie avec sa durée de validité ;
 *  - **une activité de l'élève** (`activity`) : une ligne, sa progression, son score, son temps ; ses traces dessous.
 *
 * Lues par `<TutorSessionEntry>` ; elles ne vivent pas seules.
 */
import React, { useEffect, useRef, useState } from "react"
import { Animated, Easing, Image, Platform, Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { formatMinutesSeconds, formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { tutorTraceText } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import {
    type UITutorGenerationStep,
    type UITutorReadyProposal,
    type UITutorSessionEntry,
} from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Reveal } from "../../atoms/Reveal"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { useSecondClock } from "../../hooks/useSecondClock"
import { AuroraFill } from "../../molecules/AuroraFill"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { shadowStyle } from "../../utils/shadow"

type Entry<K extends UITutorSessionEntry["kind"]> = Extract<UITutorSessionEntry, { kind: K }>

const TEXTS = {
    review: "Revoir",
    reviewing: "En relecture au centre",
    proposes: (helper: string) => `${helper} propose`,
    steps: "Les étapes",
    reason: (reason: string) => `${reason}, le détail`,
    see: "Voir",
    sent: "Envoyée",
    dismiss: "Écarter la proposition",
    traces: (count: number) => countLabel(count, "étape"),
    peek: (title: string) => `${title}, la voir au centre`,
}

const THUMB = 28
const STEP = 16

/** La génération qui avance : l'étape en cours et les secondes depuis l'envoi, toutes les étapes à la demande. */
export function GenerationProgress(props: { entry: Entry<"progress">; testID?: string }) {
    const { entry, testID } = props
    // État d'interface : les étapes dépliées.
    const [open, setOpen] = useState(false)
    const startedAt = Date.parse(entry.startedAt)
    const now = useSecondClock(Number.isNaN(startedAt) ? null : startedAt)
    const seconds = Number.isNaN(startedAt) ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
    const active =
        entry.steps.find((step) => step.state === "active") ?? entry.steps.find((step) => step.state === "todo")
    return (
        <View style={styles.progress} testID={testID} accessibilityLiveRegion="polite">
            <AuroraFill radius={12} />
            <Stack gap="2">
                <Pressable
                    onPress={() => setOpen(!open)}
                    accessibilityRole="button"
                    accessibilityLabel={TEXTS.steps}
                    aria-expanded={open}
                >
                    <Row gap="2" align="center">
                        {entry.thumbUri != null && <Image source={{ uri: entry.thumbUri }} style={styles.thumb} />}
                        <Stack gap="0" style={styles.grow}>
                            <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                                {entry.title}
                            </Text>
                            {active != null && (
                                <Text size="text-xs" color="text-muted" numberOfLines={1}>
                                    {active.label}
                                </Text>
                            )}
                        </Stack>
                        <Text size="text-xs" color="text-muted">
                            {formatMinutesSeconds(seconds)}
                        </Text>
                        <Icon name={open ? "chevron-up" : "chevron-down"} size={12} color="text-muted" />
                    </Row>
                </Pressable>
                <Reveal open={open}>
                    <Stack gap="2">
                        {entry.steps.map((step) => (
                            <Row key={step.label} gap="2" align="center">
                                <StepMark state={step.state} />
                                <Text
                                    size="text-xs"
                                    weight={step.state === "active" ? "semibold" : "regular"}
                                    color={step.state === "todo" ? "text-muted" : "text-strong"}
                                    style={styles.grow}
                                >
                                    {step.label}
                                </Text>
                            </Row>
                        ))}
                    </Stack>
                </Reveal>
                <Shimmer />
            </Stack>
        </View>
    )
}

/** Une étape : faite (cochée), en cours (elle respire), à venir (un cercle). */
function StepMark(props: { state: UITutorGenerationStep["state"] }) {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const pulse = useRef(new Animated.Value(1)).current
    useEffect(() => {
        if (props.state !== "active" || reduceMotion) return undefined
        const half = (toValue: number) =>
            Animated.timing(pulse, {
                toValue,
                duration: theme.motion.entree,
                easing: Easing.bezier(...theme.motion.easing),
                useNativeDriver: Platform.OS !== "web",
            })
        const loop = Animated.loop(Animated.sequence([half(0.35), half(1)]))
        loop.start()
        return () => loop.stop()
    }, [props.state, pulse, reduceMotion, theme.motion])
    if (props.state === "done") {
        return (
            <View style={styles.stepDone}>
                <Icon name="check" size={10} color="white" />
            </View>
        )
    }
    if (props.state === "active") {
        // L'opacité passe sur une vue nue : le style Unistyles reste sur la vue autour (contrat du DS).
        return (
            <Animated.View style={{ opacity: pulse }}>
                <View style={styles.stepActive} />
            </Animated.View>
        )
    }
    return <View style={styles.stepTodo} />
}

/** Une barre qui file sous les étapes : la durée n'est pas connue, le mouvement dit qu'elle court. */
function Shimmer() {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const x = useRef(new Animated.Value(0)).current
    useEffect(() => {
        if (reduceMotion) return undefined
        const loop = Animated.loop(
            Animated.timing(x, {
                toValue: 1,
                duration: theme.motion.entree * 3,
                easing: Easing.bezier(...theme.motion.easing),
                useNativeDriver: false,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [x, reduceMotion, theme.motion])
    if (reduceMotion) return null
    const left = x.interpolate({ inputRange: [0, 1], outputRange: ["-35%", "100%"] })
    return (
        <View style={styles.shimmerTrack}>
            <Animated.View style={[styles.shimmerBar, { left }]} />
        </View>
    )
}

/** « Ta demande · 3 propositions » : courte, actionnable ; la relecture se fait au centre. */
export function GenerationReady(props: {
    entry: Entry<"ready">
    onReview?: () => void
    onSendFirst?: () => void
    onPeek?: (proposalId: string | null) => void
    peekId?: string | null
    framed?: boolean
    testID?: string
}) {
    const { entry, onReview, onSendFirst, onPeek, peekId, framed = true, testID } = props
    const reviewing = entry.reviewing === true
    const firstReady = entry.proposals.find((proposal) => proposal.status === "ready")
    const card = (
        <View style={styles.card} testID={testID}>
            <Stack gap="2">
                <Row gap="2" align="center">
                    <View style={styles.readyIcon}>
                        <Icon name="magic" size={12} color="text-on-role" />
                    </View>
                    <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1} style={styles.grow}>
                        {entry.title}
                        <Text size="text-xs" color="text-muted">
                            {` · ${entry.caption}`}
                        </Text>
                    </Text>
                    {entry.unseen != null && <NotificationDot color="danger" size={8} />}
                </Row>
                <Stack gap="0">
                    {entry.proposals.map((proposal, position) => (
                        <ProposalPeekRow
                            key={proposal.id}
                            proposal={proposal}
                            peekId={peekId}
                            onPeek={onPeek}
                            onPress={onReview}
                            style={styles.proposalRow(proposal.id === peekId)}
                        >
                            <Text size="text-xs" weight="bold" color="text-muted" style={styles.rowNumber}>
                                {String(position + 1)}
                            </Text>
                            <Text
                                size="text-xs"
                                color={proposal.status === "ready" ? "text-strong" : "text-muted"}
                                numberOfLines={1}
                                style={styles.grow}
                            >
                                {proposal.label !== "" && (
                                    <Text size="text-xxs" weight="bold" color="role-strong" uppercase>
                                        {`${proposal.label} · `}
                                    </Text>
                                )}
                                {proposal.title}
                            </Text>
                        </ProposalPeekRow>
                    ))}
                </Stack>
                {entry.unseen != null && (
                    <Text size="text-xs" color="text-muted">
                        {entry.unseen}
                    </Text>
                )}
                {reviewing ? (
                    <Row gap="2" align="center" style={styles.reviewing}>
                        <Icon name="eye" size={12} color="role" />
                        <Text size="text-xs" weight="semibold" color="role-strong">
                            {TEXTS.reviewing}
                        </Text>
                    </Row>
                ) : (
                    <Row gap="2" align="center" style={styles.wrap}>
                        {onReview != null && (
                            <Button label={TEXTS.review} iconName="expand-alt" size="sm" onPress={onReview} />
                        )}
                        {onSendFirst != null && firstReady != null && entry.sendLabel != null && (
                            <Button label={entry.sendLabel} variant="ghost" size="sm" onPress={onSendFirst} />
                        )}
                    </Row>
                )}
            </Stack>
        </View>
    )
    // Le cadre désigne la prochaine action ; en relecture, elle est au centre.
    return reviewing || !framed ? card : <HighlightFrame>{card}</HighlightFrame>
}

/** « <champion> propose » : une activité déjà prête, à voir au centre ou à envoyer d'un geste. */
export function HelperProposal(props: {
    entry: Entry<"proposal">
    helper: ChampionName
    onSee?: () => void
    onSend?: () => void
    onDismiss?: () => void
    testID?: string
}) {
    const { entry, helper, onSee, onSend, onDismiss, testID } = props
    // État d'interface : la raison dépliée, avec sa durée de validité.
    const [open, setOpen] = useState(false)
    return (
        <View style={[styles.card, styles.proposalRadius]} testID={testID}>
            <AuroraFill radius={12} />
            <Stack gap="1">
                <Row gap="2" align="center">
                    <ChampionAvatar name={helper} size={16} />
                    <Text size="text-xxs" weight="bold" color="text-strong" uppercase style={styles.grow}>
                        {TEXTS.proposes(CHAMPIONS[helper].label)}
                    </Text>
                    <Text size="text-xxs" color="text-muted">
                        {formatTime(entry.at)}
                    </Text>
                    {onDismiss != null && entry.state === "ready" && (
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor="text-muted"
                            size={24}
                            iconSize={12}
                            onPress={onDismiss}
                            accessibilityLabel={TEXTS.dismiss}
                        />
                    )}
                </Row>
                <Row gap="2" align="center">
                    <Icon name="bolt" size={12} color="success" />
                    <Text size="text-sm" weight="semibold" color="text-strong" numberOfLines={1} style={styles.grow}>
                        {entry.title}
                    </Text>
                </Row>
                <Pressable
                    onPress={() => setOpen(!open)}
                    accessibilityRole="button"
                    accessibilityLabel={TEXTS.reason(entry.reason)}
                    aria-expanded={open}
                >
                    <Text size="text-xs" color="text-primary" numberOfLines={open ? 0 : 1}>
                        {entry.reason}
                    </Text>
                    {open && (
                        <Text size="text-xs" weight="bold" color="text-success">
                            {entry.meta}
                        </Text>
                    )}
                    {open && entry.validWhile != null && (
                        <Text size="text-xs" color="text-muted">
                            {entry.validWhile}
                        </Text>
                    )}
                </Pressable>
                {entry.state === "sent" ? (
                    <Row gap="2" align="center">
                        <Icon name="check-circle" size={14} color="success" />
                        <Text size="text-xs" weight="semibold" color="text-success">
                            {TEXTS.sent}
                        </Text>
                    </Row>
                ) : (
                    <Row gap="2" align="center" style={styles.wrap}>
                        {onSend != null && (
                            <Button
                                label={entry.sendLabel}
                                iconName="telegram-plane"
                                size="sm"
                                loading={entry.state === "sending"}
                                onPress={onSend}
                            />
                        )}
                        {onSee != null && (
                            <Button label={TEXTS.see} iconName="eye" variant="ghost" size="sm" onPress={onSee} />
                        )}
                    </Row>
                )}
            </Stack>
        </View>
    )
}

/** Le ton du score : la couleur de sa chip. */
const SCORE_VARIANT = { success: "success", warning: "warning", danger: "danger", neutral: "neutral" } as const

/** Une activité de l'élève, et ses traces rangées dessous ; celle qu'il a devant lui s'ouvre d'emblée. */
export function ActivityGroup(props: { entry: Entry<"activity">; testID?: string }) {
    const { entry, testID } = props
    const current = entry.status === "current"
    // État d'interface : le groupe déplié.
    const [open, setOpen] = useState(false)
    return (
        <View style={styles.activity(current)} testID={testID}>
            <Pressable onPress={() => setOpen(!open)} accessibilityRole="button" aria-expanded={open}>
                <Stack gap="1">
                    <Row gap="2" align="center">
                        <Icon
                            name={current ? "play" : entry.status === "done" ? "check-circle" : "clock"}
                            size={14}
                            color={current ? "role" : entry.status === "done" ? "success" : "text-muted"}
                        />
                        <Text size="text-xs" weight="bold" color="text-strong" numberOfLines={1} style={styles.grow}>
                            {entry.title}
                        </Text>
                        {entry.time != null && (
                            <Text size="text-xs" color="text-muted">
                                {entry.time}
                            </Text>
                        )}
                        <Icon name={open ? "chevron-up" : "chevron-down"} size={12} color="text-muted" />
                    </Row>
                    <Row gap="2" align="center">
                        {entry.progress != null && (
                            <ProgressBar
                                value={entry.progress}
                                height={4}
                                color={current ? "role" : "success"}
                                style={styles.grow}
                            />
                        )}
                        {entry.score != null ? (
                            <Badge text={entry.score.text} variant={SCORE_VARIANT[entry.score.tone]} tinted />
                        ) : (
                            <Text size="text-xs" color="text-muted">
                                {TEXTS.traces(entry.traces.length)}
                            </Text>
                        )}
                    </Row>
                </Stack>
            </Pressable>
            <Reveal open={open}>
                <Stack gap="2" style={styles.traces}>
                    {entry.traces.map((trace) => (
                        <Row key={trace.id} gap="2" align="start">
                            <Text size="text-xs" color="text-muted" style={styles.time}>
                                {formatTime(trace.at)}
                            </Text>
                            <Text size="text-xs" color="text-muted" style={styles.grow}>
                                {tutorTraceText(trace)}
                            </Text>
                        </Row>
                    ))}
                </Stack>
            </Reveal>
        </View>
    )
}

/**
 * Une proposition qu'on survole et qu'on touche, dans la carte « prêtes » et dans l'onglet « Fil » : survolée prête, son
 * aperçu au centre (`onPeek`) ; touchée, sa relecture (`onPress`) ; l'œil marque celle qu'on regarde. Chacun pose sa
 * typographie (`children`) et son style de rangée.
 */
export function ProposalPeekRow(props: {
    proposal: UITutorReadyProposal
    peekId?: string | null
    onPeek?: (id: string | null) => void
    onPress?: () => void
    style: React.ComponentProps<typeof Pressable>["style"]
    children: React.ReactNode
}) {
    const { proposal, peekId, onPeek, onPress, style, children } = props
    return (
        <Pressable
            onHoverIn={proposal.status === "ready" ? () => onPeek?.(proposal.id) : undefined}
            onHoverOut={() => onPeek?.(null)}
            onPress={onPress}
            disabled={onPress == null}
            accessibilityRole="button"
            accessibilityLabel={TEXTS.peek(proposal.title)}
            style={style}
        >
            {children}
            {proposal.id === peekId && <Icon name="eye" size={12} color="role" />}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    grow: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" as const },
    time: { width: 36 },
    progress: {
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "card"),
    },
    thumb: { width: THUMB, height: THUMB, borderRadius: theme.radius.sm },
    stepDone: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors.role,
    },
    stepActive: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        borderWidth: 4,
        borderColor: theme.colors.role,
        backgroundColor: theme.colors.surface,
    },
    stepTodo: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        borderWidth: 1.5,
        borderColor: theme.colors.border,
    },
    shimmerTrack: {
        height: 4,
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors["surface-subtle"],
    },
    shimmerBar: {
        position: "absolute" as const,
        top: 0,
        bottom: 0,
        width: "35%",
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.role,
    },
    card: {
        padding: theme.spacing["3"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "card"),
    },
    /** « <champion> propose » : la seule carte colorée du fil, son aurore à plat sur la surface, un rayon plus serré. */
    proposalRadius: { borderRadius: theme.radius.md },
    readyIcon: {
        width: 22,
        height: 22,
        borderRadius: theme.radius.md,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors["role-fill"],
    },
    proposalRow: (peeked: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["1"],
        paddingVertical: 2,
        borderRadius: theme.radius.sm,
        backgroundColor: peeked ? theme.colors["surface-subtle"] : "transparent",
    }),
    rowNumber: { width: 12 },
    reviewing: {
        alignSelf: "flex-start" as const,
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-subtle"],
    },
    activity: (current: boolean) => ({
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-raised"],
        borderWidth: 1,
        borderColor: current ? theme.colors.role : theme.colors["border-subtle"],
    }),
    traces: {
        marginTop: theme.spacing["2"],
        paddingTop: theme.spacing["2"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
    },
}))
